import m from './module';
import { createError } from '../error';
import PartialClaim from '../model/PartialClaim';
import Claim from '../model/Claim';

const getPartialClaimsUrl= '/partial_claims/{{staffing_users_id}}';

class PartialClaimService {
  constructor(
    $log,
    $q,
    APIService,
    DataSyncService,
    ClaimService,
    LogService,
    TaskQueueService,
    DateTimeService
  ) {
    "ngInject";
    this.$log            = $log;
    this.$q              = $q;
    this.APIService      = APIService;
    this.DataSyncService = DataSyncService;
    this.ClaimService    = ClaimService;
    this.LogService      = LogService;
    this.DateTimeService = DateTimeService;
    this.TaskQueueService = TaskQueueService;
  }

  /**
   * @param data
   *    Should be formatted:
   *    {
   *      events_id: 123,
   *      staffing_users_id: 456,
   *      data: {
   *        primaryClaim: { .. serialized primary claim data .. },
   *        secondaryClaims: [{ .. serialized secondary claim data .. }, ..],
   *        positionClaimDate: YYYY-MM-DD,
   *        positionActualStart: HH:MM:SS, (in event timezone)
   *        positionActualEnd: HH:MM:SS, (in event timezone)
   *      }
   *    }
   * @param config
   * @returns object
   *    {
   *      eventId: 123,
   *      staffUserId: 456,
   *      primaryClaim: Claim,
   *      secondaryClaims: [Claim, ...],
   *      positionClaimDate: Date,
   *      positionActualStart: Date,
   *      positionActualEnd: Date,
   *      positionMealBreakStart: Date,
   *      positionMealBreakEnd: Date,
   *      positionRestBreaksStart: {},
   *      positionRestBreaksEnd: {},
   *      positionMealBreakAcknowledgement: 1,
   *      positionRestBreakAcknowledgement: 1,
   *      positionShiftSegmentId: 123,
   *    }
   */
  create(data, config = null) {
    const promises = [];
    data = data || {};

    const nestedData = data.data || {};
    const primaryClaimData = nestedData.primaryClaim || null;
    const secondaryClaimDatas = nestedData.secondaryClaims || [];
    const eventId = data.events_id;
    const staffUserId = data.staffing_users_id;
    let primaryClaim = null;
    let positionClaimDate = null;
    let positionActualStart = null;
    let positionActualEnd = null;
    let positionMealBreakStart = null;
    let positionMealBreakEnd = null;
    let positionRestBreaksStart = {}
    let positionRestBreaksEnd = {}
    let positionMealBreakAcknowledgement = null
    let positionRestBreakAcknowledgement = null
    const positionShiftSegmentId = nestedData.positionShiftSegmentId;
    const secondaryClaims = [];

    if (primaryClaimData && !(primaryClaimData instanceof Claim)) {
      promises.push(
        this.ClaimService.create(primaryClaimData)
          .then(result => {
            primaryClaim = result;
            return primaryClaim;
          })
          .then(primaryClaim => {
            const event = primaryClaim.event;

            // Parse dates and times
            const eventTimezoneName = event && event.timezoneName;
            if (!eventTimezoneName) {
              this.LogService.debug(`PartialClaimService.create() no event timezone for data`, data);
            }

            // Parse position claim date in local timezone
            if (nestedData.positionClaimDate) {
              positionClaimDate = this.DateTimeService.parseDate(nestedData.positionClaimDate);
            }

            if (nestedData.positionMealBreakAcknowledgement) {
              positionMealBreakAcknowledgement = nestedData.positionMealBreakAcknowledgement
            }
            if (nestedData.positionRestBreakAcknowledgement) {
              positionRestBreakAcknowledgement = nestedData.positionRestBreakAcknowledgement
            }

            // Parse start/end times in event timezone
            if (nestedData.positionActualStart) {
              positionActualStart = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(nestedData.positionActualStart, eventTimezoneName);
            }
            if (nestedData.positionActualEnd) {
              positionActualEnd = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(nestedData.positionActualEnd, eventTimezoneName);
            }
            if (nestedData.positionMealBreakStart) {
              positionMealBreakStart = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(nestedData.positionMealBreakStart, eventTimezoneName);
            }
            if (nestedData.positionMealBreakEnd) {
              positionMealBreakEnd = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(nestedData.positionMealBreakEnd, eventTimezoneName);
            }
            if (nestedData.positionRestBreaksStart && Object.keys(nestedData.positionRestBreaksStart).length > 0) {
              for (const property in nestedData.positionRestBreaksStart) {
                positionRestBreaksStart[property] = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(nestedData.positionRestBreaksStart[property], eventTimezoneName);
              }
            }
            if (nestedData.positionRestBreaksEnd && Object.keys(nestedData.positionRestBreaksEnd).length > 0) {
              for (const property in nestedData.positionRestBreaksEnd) {
                positionRestBreaksEnd[property] = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(nestedData.positionRestBreaksEnd[property], eventTimezoneName);
              }
            }

            return primaryClaim;
          })
      )
    } else {
      primaryClaim = primaryClaimData;
    }

    secondaryClaimDatas.forEach(secondaryClaimData => {
      if (secondaryClaimData instanceof Claim) {
        secondaryClaims.push(secondaryClaimData);
      } else if (secondaryClaimData) {
        promises.push(
          this.ClaimService.create(secondaryClaimData)
            .then(secondaryClaim => {
              secondaryClaims.push(secondaryClaim);
            })
            .catch(err => {
              this.LogService.error(`Error creating secondary claim from partial claim:`, err);
              return this.$q.reject(err);
            })
        )
      } else {
        this.LogService.warn(`PartialClaimService.create() got empty secondary claim`);
      }
    });

    return this.$q
      .all(promises)
      .then(() => {
        return new PartialClaim({
          primaryClaim,
          secondaryClaims,
          eventId,
          staffUserId,
          positionClaimDate,
          positionActualStart,
          positionActualEnd,
          positionMealBreakStart,
          positionMealBreakEnd,
          positionRestBreaksStart,
          positionRestBreaksEnd,
          positionMealBreakAcknowledgement,
          positionRestBreakAcknowledgement,
          positionShiftSegmentId,
        });
      })
      .catch(err => {
        this.LogService.error("Error creating partial response:", err);
        return this.$q.reject(err);
      });
  }

  /**
   *
   * @param partialClaim PartialClaim
   * @returns object
   *    {
   *      events_id: 123,
   *      staffing_users_id: 456,
   *      data: {
   *        primaryClaim: { .. serialized primary claim data .. },
   *        secondaryClaims: [{ .. serialized secondary claim data .. }, ..],
   *        positionClaimDate: YYYY-MM-DD,
   *        positionActualStart: HH:MM:SS,
   *        positionActualEnd: HH:MM:SS,
   *        positionMealBreakStart: HH:MM:SS,
   *        positionMealBreakEnd: HH:MM:SS,
   *        positionRestBreaksStart: {.. #.HH:MM:SS, #.HH:MM:SS, ..},
   *        positionRestBreaksEnd: {.. #.HH:MM:SS, #.HH:MM:SS, ..},
   *        positionMealBreakAcknowledgement: 1,
   *        positionRestBreakAcknowledgement: 1,
   *        positionShiftSegmentId: 123,
   *      }
   *    }
   */
  serialize(partialClaim) {
    const eventId = partialClaim.eventId || null;
    const staffUserId = partialClaim.staffUserId;
    const primaryClaim = partialClaim.primaryClaim || null;
    const secondaryClaims = partialClaim.secondaryClaims || [];
    const primaryClaimData = primaryClaim
      ? this.ClaimService.serialize(primaryClaim)
      : null;
    const secondaryClaimDatas = secondaryClaims.map(claim => this.ClaimService.serialize(claim));

    // Convert timezone
    const event = (primaryClaim && primaryClaim.event)
      || (secondaryClaims && secondaryClaims[0] && secondaryClaims[0].event);
    const eventTimezoneName = event && event.timezoneName;
    if (!eventTimezoneName) {
      this.LogService.debug(`PartialClaimService.serialize() no event timezone for partial claim`, partialClaim);
    }
    const positionClaimDate = partialClaim.positionClaimDate
      ? this.DateTimeService.serializeDateForAPI(partialClaim.positionClaimDate)
      : null;
    const positionActualStart = partialClaim.positionActualStart
      ? this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(partialClaim.positionActualStart, eventTimezoneName)
      : null;
    const positionActualEnd = partialClaim.positionActualEnd
      ? this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(partialClaim.positionActualEnd, eventTimezoneName)
      : null;
    const positionMealBreakStart = partialClaim.positionMealBreakStart
      ? this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(partialClaim.positionMealBreakStart, eventTimezoneName)
      : null;
    const positionMealBreakEnd = partialClaim.positionMealBreakEnd
      ? this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(partialClaim.positionMealBreakEnd, eventTimezoneName)
      : null;

    let positionRestBreaksStart = {}
    if (partialClaim.positionRestBreaksStart && Object.keys(partialClaim.positionRestBreaksStart).length > 0) {
      for (const property in partialClaim.positionRestBreaksStart) {
        console.log('inside for loop property and value:', property, partialClaim.positionRestBreaksStart[property])
        positionRestBreaksStart[property] = this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(partialClaim.positionRestBreaksStart[property], eventTimezoneName);
      }
    }
    let positionRestBreaksEnd = {}
    if (partialClaim.positionRestBreaksEnd && Object.keys(partialClaim.positionRestBreaksEnd).length > 0) {
      for (const property in partialClaim.positionRestBreaksEnd) {
        positionRestBreaksEnd[property] = this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(partialClaim.positionRestBreaksEnd[property], eventTimezoneName);
      }
    }

    const positionMealBreakAcknowledgement = partialClaim.positionMealBreakAcknowledgement
    const positionRestBreakAcknowledgement = partialClaim.positionRestBreakAcknowledgement

    const positionShiftSegmentId = partialClaim.positionShiftSegmentId;

    return {
      events_id: eventId,
      staffing_users_id: staffUserId,
      data: {
        primaryClaim: primaryClaimData,
        secondaryClaims: secondaryClaimDatas,
        positionClaimDate,
        positionActualStart,
        positionActualEnd,
        positionMealBreakStart,
        positionMealBreakEnd,
        positionRestBreaksStart,
        positionRestBreaksEnd,
        positionMealBreakAcknowledgement,
        positionRestBreakAcknowledgement,
        positionShiftSegmentId,
      },
    };
  }

  /**
   * Create a PartialClaim object from a primary claim, secondary claims, eventId
   * @param primaryClaim Claim
   * @param secondaryClaims
   * @param eventId
   * @param staffUserId
   * @param position Position
   * @returns PartialClaim
   */
  createFromClaims(primaryClaim, secondaryClaims, eventId, staffUserId, position) {
    const positionClaimDate = position && position.claim_date;
    const positionActualStart = position && position.actual_start;
    const positionActualEnd = position && position.actual_end;
    const positionMealBreakStart = position && position.meal_break_start;
    const positionMealBreakEnd = position && position.meal_break_end;
    const positionShiftSegmentId = position && position.shiftSegment && position.shiftSegment.id;
    const positionRestBreaksStart = position.rest_break_start || {}
    const positionRestBreaksEnd = position.rest_break_end || {}
    const positionMealBreakAcknowledgement = position.meal_break_acknowledgement || null
    const positionRestBreakAcknowledgement = position.rest_break_acknowledgement || null

    return new PartialClaim({
      eventId,
      staffUserId,
      primaryClaim,
      secondaryClaims,
      positionClaimDate,
      positionActualStart,
      positionActualEnd,
      positionMealBreakStart,
      positionMealBreakEnd,
      positionRestBreaksStart,
      positionRestBreaksEnd,
      positionMealBreakAcknowledgement,
      positionRestBreakAcknowledgement,
      positionShiftSegmentId,
    });
  }

  updateStaffUserPartialClaimsForPosition(staffUser, position, partialClaim) {
    const eventId = position.events_id;

    this.LogService && this.LogService.debug(`PartialClaimService.updateStaffUserPartialClaimsForPosition() eventId=${eventId} partialClaim:`, partialClaim);

    // Filter out existing partial claims with this event id
    const filteredPartialClaims = staffUser.partialClaims.filter(partialClaim => parseInt(partialClaim.eventId) !== parseInt(eventId));

    // Append this partial claim
    filteredPartialClaims.push(partialClaim);

    // Overwrite staffUser.partialClaims
    staffUser.partialClaims = filteredPartialClaims;

    staffUser._updateStaffUserPartialClaimsForPosition = staffUser._updateStaffUserPartialClaimsForPosition ? staffUser._updateStaffUserPartialClaimsForPosition++ : 1;
  }

  getForPosition(staffUser, position) {
    staffUser._getForPosition = staffUser._getForPosition ? staffUser._getForPosition++ : 1;
    const eventId = position.events_id;
    const partialClaim = staffUser.partialClaims.find(partialClaim => parseInt(partialClaim.eventId) === parseInt(eventId));
    if (partialClaim) {
      if (partialClaim.positionClaimDate && !position.claim_date) {
        position.claim_date = partialClaim.positionClaimDate;
      }
      if (partialClaim.positionActualStart && position.actual_start !== partialClaim.positionActualStart) {
        position.actual_start = partialClaim.positionActualStart;
      }
      if (partialClaim.positionActualEnd && position.actual_end !== partialClaim.positionActualEnd) {
        position.actual_end = partialClaim.positionActualEnd;
      }
      if (partialClaim.positionMealBreakStart && position.meal_break_start !== partialClaim.positionMealBreakStart) {
        position.meal_break_start = partialClaim.positionMealBreakStart;
      }
      if (partialClaim.positionMealBreakEnd && position.meal_break_end !== partialClaim.positionMealBreakEnd) {
        position.meal_break_end = partialClaim.positionMealBreakEnd;
      }

      position.rest_break_start = {}
      position.rest_break_end = {}
      if (position.rest_break_eligible && position.rest_break_expected_qty) {
        if (partialClaim.positionRestBreaksStart && Object.keys(partialClaim.positionRestBreaksStart).length > 0) {
          position.rest_break_start = partialClaim.positionRestBreaksStart
        }
        if (partialClaim.positionRestBreaksEnd && Object.keys(partialClaim.positionRestBreaksEnd).length > 0) {
          position.rest_break_end = partialClaim.positionRestBreaksEnd
        }
      }

      if (partialClaim.positionMealBreakAcknowledgement) {
        position.meal_break_acknowledgement = partialClaim.positionMealBreakAcknowledgement
      }
      if (partialClaim.positionRestBreakAcknowledgement) {
        position.rest_break_acknowledgement = partialClaim.positionRestBreakAcknowledgement
      }

      if (partialClaim.positionShiftSegmentId) {
        let allShiftSegments = staffUser.shift_segments;
        let shiftSegment = allShiftSegments.find(s => parseInt(s.id) === parseInt(partialClaim.positionShiftSegmentId));
        if (shiftSegment) {
          position.shiftSegment = shiftSegment;
        } else {
          this.LogService.warn(`PartialClaimService.getForPosition() events_id=${position.events_id} did not find shift segment ${partialClaim.positionShiftSegmentId}`);
        }
      }
    }
    return partialClaim;
  }

  removeForPosition(staffUser, position) {
    const eventId = position.events_id;
    staffUser.partialClaims = staffUser.partialClaims.filter(partialClaim => parseInt(partialClaim.eventId) !== parseInt(eventId));
    staffUser._removeForPosition = staffUser._removeForPosition ? staffUser._removeForPosition++ : 1;

    let task = this.TaskQueueService.createTask('PartialClaimDeleteTask');
    task.staffingUsersId = staffUser.id;
    task.eventsId = eventId;

    return task.execute();
  }

  getAllForStaffingUser(staffUser, config = null) {
    const staffUserId = staffUser.id;
    staffUser._getAllForStaffingUser = staffUser._getAllForStaffingUser ? staffUser._getAllForStaffingUser++ : 1;

    const path = getPartialClaimsUrl.replace('{{staffing_users_id}}', staffUserId);
    return this.DataSyncService.getMultipleObjects('PartialClaims', path, data => this.create(data, config), config)
      .then(partialClaims => {
        // Ignore any empty partial claims (no primary or secondary claims)
        return partialClaims.filter(partialClaim => {
          if (partialClaim.primaryClaim || (partialClaim.secondaryClaims && partialClaim.secondaryClaims.length > 0)) {
            return true;
          }
          this.LogService.warn(`PartialClaimService.getAllForStaffUser() ignoring empty partial claim server sent:`, partialClaim);
          return false;
        });
      })
      .then(partialClaims => {
        return partialClaims;
      })
      .catch(response => {
        this.LogService.error("Error retrieving partial claims (PartialClaimService.getAllForStaffingUser):", response);
        return this.$q.reject(createError({
          error: 'ErrorRetrievingPartialClaims',
          message: 'Error retrieving partial claimsfrom server',
          response,
        }));
      });
  }
}

export default PartialClaimService;

m.service('PartialClaimService', PartialClaimService);
